import { PlusIcon } from "icons/outline";
import { IconButton, cn } from "ui";

import HeaderTabs from "../../components/HeaderTabs";
import { useRouter } from "next/router";
import PlanCard from "../../components/Subscription/PlanCard";
import EmptyPlans from "../../components/Subscription/EmptyPlans";
import { plansTabs as tabs } from "../../components/Subscription/utils";
import NoResults from "../../components/NoResults/NoResults";
import Loading from "../../components/Loading";

import { usePaginationParams } from "../../hooks/usePaginationParams";
import { useMoneyHashQuery } from "../../hooks/useMoneyHashQuery";
import { useRestoreScrollOnFetching } from "../../hooks/useRestoreScrollOnFetching";

import Layout from "../../components/Layout";
import { Pagination } from "../../components/Pagination";
import {
  GlobalSearch,
  useGlobalSearchQuery,
} from "../../components/GlobalSearch";
import { axiosApiInstance } from "../api/axios";
import { ErrorPage } from "../../components/ErrorPage";
import { Filters } from "../../components/newFilters";

export default function PlansPage() {
  const router = useRouter();
  const [paginationParams] = usePaginationParams();
  const searchQuery = useGlobalSearchQuery();

  const {
    data: plans,
    count,
    isLoading,
    isFetching,
    error,
    isError,
    listMeta,
  } = useMoneyHashQuery({
    queryKey: ["subscriptionPlans", { ...paginationParams, ...router.query }],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get("/d/subscriptions/plans/", {
          params: {
            ...paginationParams,
            ...router.query,
          },
          signal,
        })
        .then((res) => res.data),
    keepPreviousData: true,
  });
  const scrollRef = useRestoreScrollOnFetching(isFetching);

  return (
    <Layout ref={scrollRef} metaTitle="Plans">
      <div className="w-full">
        <div className="flex justify-between">
          <div className="w-full">
            <div className="flex flex-col">
              <div className="flex flex-col">
                <span className="text-lg font-bold text-foundations-boldest">
                  Manage all your plans
                </span>
              </div>

              <div className="flex flex-row justify-between pt-2">
                <span
                  className={cn(
                    "text-sm font-normal text-foundations-subtle sm:w-3/5"
                  )}
                >
                  Create a new plans and view existing ones to make sure you are
                  offering the best to your customers.
                </span>
              </div>
            </div>

            <HeaderTabs tabs={tabs} />
          </div>
          <div>
            {listMeta?.add_enabled && (
              <IconButton onClick={() => router.push(`/plans/add`)}>
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            )}
          </div>
        </div>

        <GlobalSearch scope="Plans" />
        {isError && <ErrorPage error={error} />}
        {!isError && (
          <>
            <div className="mx-auto max-w-7xl">
              {!isLoading && (
                <div className="mt-8">
                  <span className="text-sm font-medium text-foundations-bold">
                    {count} subscription plans
                  </span>
                </div>
              )}

              {listMeta?.filter_enabled && (
                <div className="mt-3">
                  <Filters
                    filters={listMeta.filter_types}
                    recentStoreKey="plans"
                  />
                </div>
              )}

              <div className="flex w-full flex-col items-center">
                {isLoading ? (
                  <span className="pt-6">
                    <Loading />
                  </span>
                ) : (
                  <div
                    className={cn(
                      "mt-4 w-full",
                      !!plans.length
                        ? "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 "
                        : ""
                    )}
                  >
                    {plans.map(
                      (
                        {
                          columns: {
                            amount,
                            currency,
                            id,
                            name,
                            recurrency,
                            recurrency_unit,
                          },
                        },
                        index
                      ) => (
                        <PlanCard
                          key={index}
                          data={{
                            id,
                            title: name,
                            pricing: {
                              amount,
                              currency,
                              recurrency,
                              recurrency_unit,
                            },
                          }}
                          link={`/plans/${id}`}
                        />
                      )
                    )}

                    {!plans.length && !isLoading && !searchQuery && (
                      <EmptyPlans
                        onClickAdd={() => {
                          router.push(`/plans/add`);
                        }}
                      />
                    )}

                    {!plans.length && !!searchQuery && (
                      <NoResults
                        isFiltering={false}
                        addEnabled={false}
                        tableName="subscription plan"
                      />
                    )}
                  </div>
                )}

                {count > 1 && !isLoading ? (
                  <Pagination
                    total={count}
                    onPageChange={({ _, page, limit }) => {
                      if (isLoading) return;

                      router.replace({
                        query: { ...router.query, page, limit },
                      });
                    }}
                  />
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
PlansPage.parentPath = "/plans";
